import { useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataGrid,
  PageHeader,
  TotalRow,
  type ColumnDef,
} from '@biovet/ui';
import {
  RECORD_SETS,
  type RecordColumn,
  type RecordRow,
  type RecordSet,
} from '../lib/design-data.js';
import { exportReport } from '../lib/export.js';
import { printReport, type ReportTable } from '../lib/print.js';


interface Normalized {
  cells: string[];
  head: boolean;
  bold: boolean;
  emphasis: boolean;
}

function normalize(row: RecordRow): Normalized {
  if (Array.isArray(row)) {
    return { cells: row, head: false, bold: false, emphasis: false };
  }
  return {
    cells: row.c,
    head: row.head ?? false,
    bold: row.bold ?? false,
    emphasis: row.emphasis ?? false,
  };
}

export function reportOf(set: RecordSet, chipLabel?: string): ReportTable {
  return {
    title: set.title,
    fileName: set.title,
    meta: [chipLabel && chipLabel !== 'All' ? chipLabel : null, `${set.rows.length} rows`]
      .filter(Boolean)
      .join(' · '),
    columns: set.cols.map((c) => ({
      label: c.label,
      align: c.align === 'right' ? 'right' : 'left',
      width: c.width,
    })),
    rows: [
      ...set.rows.map((row) => {
        const r = normalize(row);
        return { cells: r.cells, head: r.head, total: r.bold || r.emphasis };
      }),
      ...(set.total ? [{ cells: set.total, total: true }] : []),
    ],
  };
}

export function RecordsScreen({ recordKey }: { recordKey: string }) {
  const set = RECORD_SETS[recordKey];
  if (!set) {
    return (
      <PageHeader
        title="No dataset for this screen"
        description={`\`${recordKey}\` is in the nav but has no entry in design-data.ts.`}
      />
    );
  }
  return <RecordGrid set={set} />;
}

export interface RecordGridProps {
  set: RecordSet;
  loading?: boolean;
  error?: string;
  chip?: number;
  onChipChange?: (index: number) => void;
  actions?: ReactNode;
  noReport?: boolean;
  children?: ReactNode;
  onRowSelect?: (index: number) => void;
  selectedRow?: number | null;
  emptyTitle?: string;
  hideGrid?: boolean;
}

export function RecordGrid({
  set,
  loading = false,
  error,
  chip,
  onChipChange,
  actions,
  children,
  onRowSelect,
  selectedRow,
  emptyTitle = 'Nothing in this register yet',
  hideGrid = false,
  noReport = false,
}: RecordGridProps) {
  const [localChip, setLocalChip] = useState(0);
  const activeChip = chip ?? localChip;
  const pickChip = onChipChange ?? setLocalChip;

  const rows = set.rows.map((row, index) => ({ ...normalize(row), index }));
  const columns = set.cols.map(toColumn);

  return (
    <>
      <PageHeader
        breadcrumb={set.crumb}
        title={set.title}
        description={set.subtitle}
        actions={
          <>
            {actions}
            {!noReport && (
              <>
                <Button size="sm" onClick={() => printReport(reportOf(set, set.chips[activeChip]))}>
                  Print
                </Button>
                {}
                <Button
                  size="sm"
                  onClick={() => exportReport(reportOf(set, set.chips[activeChip]))}
                >
                  Export XLSX
                </Button>
              </>
            )}
          </>
        }
        meta={
          set.chips.length > 0 && !hideGrid ? (
            <div data-noprint="1" className="flex flex-wrap items-center gap-1.5">
              {set.chips.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => pickChip(i)}
                  aria-pressed={i === activeChip}
                  className={
                    i === activeChip
                      ? 'cursor-pointer rounded-[var(--radius-pill)] border border-transparent bg-[var(--accent-soft)] px-3 py-1 text-[12px] font-medium text-[var(--accent)]'
                      : 'cursor-pointer rounded-[var(--radius-pill)] border border-[var(--line)] bg-[var(--surface)] px-3 py-1 text-[12px] text-[var(--ink-2)] hover:border-[var(--ink-3)] hover:text-[var(--ink)]'
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          ) : undefined
        }
      />

      {error && (
        <Callout tone="danger" title="This report could not be loaded">
          {error}
        </Callout>
      )}

      {children}

      {hideGrid ? null : (
        <Card flush data-rpt="1">
          <DataGrid<Normalized & { index: number }>
            data={rows}
            columns={columns}
            loading={loading}
            emptyTitle={emptyTitle}
            aria-label={set.title}
            onRowClick={onRowSelect ? (row) => onRowSelect(row.index) : undefined}
            rowClassName={(row) =>
              [
                row.head && 'bg-[var(--surface-2)] font-medium',
                row.bold && !row.emphasis && 'font-semibold',
                row.emphasis && 'bg-[var(--surface-2)] font-semibold',
                selectedRow === row.index && 'bg-[var(--accent-soft)]',
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            footer={
              set.total ? (
                <TotalRow
                  cells={set.total.map((content, i) => ({
                    content,
                    align: set.cols[i]?.align,
                    numeric: set.cols[i]?.mono,
                    width: set.cols[i]?.width,
                  }))}
                />
              ) : undefined
            }
          />
        </Card>
      )}
    </>
  );
}

function toColumn(
  col: RecordColumn,
  index: number,
): ColumnDef<Normalized & { index: number }, string> {
  return {
    id: String(index),
    header: col.label,
    accessorFn: (row) => row.cells[index] ?? '',
    meta: { align: col.align, numeric: col.mono, width: col.width },
    cell: (ctx) => {
      const value = ctx.getValue();
      if (ctx.row.original.head) return index === 0 ? value : null;
      if (!value || value === '—')
        return <span className="text-[var(--ink-3)]">{value || '—'}</span>;
      if (col.badge) {
        const days = /^(\d+) days$/.exec(value);
        if (days) {
          return <Badge tone={Number(days[1]) >= 60 ? 'bad' : 'warn'}>{value}</Badge>;
        }
        return <Badge status={toStatusEnum(value)}>{value}</Badge>;
      }
      return value;
    },
  };
}

function toStatusEnum(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}
